import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UseQueryExecute } from 'urql';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownFill, InfoOutline } from '@lyearn/icons';
import { Button, Popover, Stack, Typography } from '@lyearn/ui';
import { useAppConfig } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import { ArticleInfoFragment } from '@/modules/corporate/modules/article/graphql/fragments/generated/ArticleContentInfo';
import { useUpdateArticleMutation } from '@/modules/corporate/modules/article/graphql/mutation/generated/UpdateArticle';
import { CourseLanguageInfoFragment } from '@/modules/corporate/modules/course/graphql/fragments/generated/CourseLanguageInfo';
import { useUpsertCourseMutation } from '@/modules/corporate/modules/course/graphql/queries/generated/upsertCourse';
import { getContentUrl } from '@/modules/corporate/modules/path/utils';
import { QuizLanguageInfoFragment } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/fragments/generated/QuizLanguageInfo';
import { useQuizEditorUpdateQuizMutation } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/mutations/generated/updateQuiz';
import { useUpdateSurveyMutation } from '@/modules/corporate/modules/survey/views/SurveyEditor/graphql/mutations/generated/updateSurvey';
import { SurveyLanguageInfoFragment } from '@/modules/corporate/modules/survey/views/SurveyEditor/graphql/queries/generated/surveyLanguageInfo';
import {
  CourseContentType,
  JobEntityStatusEnum,
  LanguageEnum,
  Maybe,
  ResourceStatusEnum,
  ResourceTypeEnum,
} from '@/types/schema';

import DropdownItem from './DropdownItem';

export interface LanguageSelectorProps {
  className?: string;
  localizedContents?: {
    contents: {
      edges?: Array<{ node: { id: string; isSyncedContentVerified?: boolean | null } | {} }> | null;
    };
  };
  content?:
    | CourseLanguageInfoFragment
    | QuizLanguageInfoFragment
    | ArticleInfoFragment
    | SurveyLanguageInfoFragment;
  refetchQuery?: UseQueryExecute;
  refetchLocalizedContent?: UseQueryExecute;
  jobStatus: Record<LanguageEnum, JobEntityStatusEnum>;
}
export const INFO_ICON_FILL = 'var(--icon-primary)' as const;

export const getLanguageName = (language: LanguageEnum): string => {
  return language?.charAt(0).toUpperCase() + language?.slice(1).toLowerCase();
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className,
  localizedContents,
  content = {} as
    | CourseLanguageInfoFragment
    | QuizLanguageInfoFragment
    | ArticleInfoFragment
    | SurveyLanguageInfoFragment,
  refetchQuery,
  refetchLocalizedContent,
  jobStatus,
}) => {
  const { t } = useTranslation();

  const navigation = useNavigation();
  const AppConfig = useAppConfig();
  const [, upsertCourse] = useUpsertCourseMutation();
  const [, updateArticle] = useUpdateArticleMutation();
  const [, updateSurvey] = useUpdateSurveyMutation();
  const [open, { on, off, toggle }] = useToggle(false);

  const supportedLanguages = AppConfig?.site?.supportedLanguages;
  const {
    languageType,
    languageConfigs,
    sourceContent,
    isLocalizedContent,
    type,
    defaultLanguageType,
    id,
    status,
  } = content;

  const [currentLanguage, setCurrentLanguage] = useState<Maybe<LanguageEnum> | undefined>();
  const [, updateQuiz] = useQuizEditorUpdateQuizMutation();

  const defaultLanguage = useMemo(() => {
    if (!isLocalizedContent) {
      return defaultLanguageType ?? undefined;
    }
    // @ts-ignore
    return sourceContent?.defaultLanguageType ?? undefined;
  }, [isLocalizedContent, defaultLanguageType, sourceContent]);

  const showDraftInfo = useMemo(() => {
    return !isLocalizedContent && status == ResourceStatusEnum.Draft;
  }, [status, isLocalizedContent]);

  const onUpdateQuiz = useCallback(
    async (language: any) => {
      const updateContent = await updateQuiz({
        data: {
          id: content.id,
          defaultLanguageType: language,
        },
      });
    },
    [content?.id, updateQuiz],
  );

  const onUpdateCourse = useCallback(
    async (language: any) => {
      const updatedCourse = await upsertCourse({
        course: {
          courseId: content.id,
          defaultLanguageType: language,
        },
      });
    },
    [content?.id, upsertCourse],
  );

  const onUpdateArticle = useCallback(
    async (language: any) => {
      const updateContent = await updateArticle({
        article: {
          articleId: content.id,
          defaultLanguageType: language,
        },
      });
    },
    [content?.id, t, updateArticle],
  );

  const onUpdateSurvey = useCallback(
    async (language: any) => {
      const updateContent = await updateSurvey({
        data: {
          id: content.id,
          defaultLanguageType: language,
        },
      });
    },
    [content?.id, t, updateSurvey],
  );

  const onChangeDefaultLanguage = useCallback(
    async (language: any) => {
      if (type == ResourceTypeEnum.Course) {
        await onUpdateCourse(language);
      } else if (type == ResourceTypeEnum.Article) {
        await onUpdateArticle(language);
      } else if (type == ResourceTypeEnum.Quiz) {
        await onUpdateQuiz(language);
      } else if (type == ResourceTypeEnum.Survey) {
        await onUpdateSurvey(language);
      }
    },
    [onUpdateCourse, onUpdateQuiz, onUpdateArticle, type],
  );

  useEffect(() => {
    if (open) {
      refetchQuery?.();
      if (!isLocalizedContent) refetchLocalizedContent?.();
    }
  }, [open, refetchQuery, refetchLocalizedContent, isLocalizedContent]);

  useEffect(() => {
    if (!currentLanguage) {
      setCurrentLanguage(languageType ?? defaultLanguageType);
    }
  }, [languageType, defaultLanguageType, currentLanguage, setCurrentLanguage]);

  const avaliableLanguageDropDown: LanguageEnum[] = useMemo(() => {
    if (isLocalizedContent) {
      // @ts-ignore
      const languages: LanguageEnum[] = sourceContent?.languageConfigs?.map(
        (languageConfig: any) => languageConfig.languageType,
      );
      if (!languages?.find((langauge) => langauge == defaultLanguage)) {
        languages?.push(defaultLanguage);
      }
      return languages;
    }
    return supportedLanguages as LanguageEnum[];
  }, [supportedLanguages, sourceContent, isLocalizedContent, defaultLanguage]);

  const onToggle_ = useCallback(
    (open?: boolean) => {
      toggle(open);
      return;
    },
    [toggle],
  );

  const localizedContentIds = useMemo<Partial<Record<LanguageEnum, string>>>(
    () => ({
      [defaultLanguage]: isLocalizedContent ? (sourceContent as CourseContentType)?.id || '' : id,
    }),
    [isLocalizedContent, id, sourceContent, defaultLanguage],
  );

  const adaptedLanguageConfigs = content?.isLocalizedContent
    ? (sourceContent as CourseContentType)?.languageConfigs
    : languageConfigs;

  const onNavigationToLanguage = useCallback(
    async (contentId?: string) => {
      if (!contentId || !content.type) return;
      const { edit: editUrl } = getContentUrl({ id: contentId, type: content.type });
      await navigation.replace(editUrl, { appendQuery: false });
      if (content.type == ResourceTypeEnum.Path || content.type == ResourceTypeEnum.Survey) {
        window.location.reload();
      }
    },
    [content.type, navigation],
  );

  return (
    <Stack className={className}>
      <Popover open={open} onOpenChange={onToggle_}>
        <Popover.Button>
          <Button className="!pl-12 !pr-8" shape="square" variant="ghost">
            <Stack alignItems="center" direction="row" gapX={4}>
              <Typography variant="label-02">
                {t('Course:Language', {
                  language: getLanguageName(currentLanguage || LanguageEnum.English),
                })}
              </Typography>
              <ArrowIosDownFill fillColorToken="icon-primary" height={20} width={20} />
            </Stack>
          </Button>
        </Popover.Button>
        <Popover.Content>
          <Stack className="p-8 w-[31rem]" direction="col" gapY={2}>
            {avaliableLanguageDropDown
              ?.filter((language) => language !== (currentLanguage ?? LanguageEnum.English))
              ?.map((language, index) => (
                <DropdownItem
                  key={index}
                  adaptedLanguageConfigs={adaptedLanguageConfigs}
                  content={content}
                  defaultLanguage={defaultLanguage}
                  isLocalizedContent={isLocalizedContent}
                  jobStatus={jobStatus}
                  language={language}
                  localizedContentIds={localizedContentIds}
                  localizedContents={localizedContents}
                  off={off}
                  refetchLocalizedContent={refetchLocalizedContent}
                  refetchQuery={refetchQuery}
                  setCurrentLanguage={setCurrentLanguage}
                  sourceContent={sourceContent}
                  onChangeDefaultLanguage={onChangeDefaultLanguage}
                  onNavigationToLanguage={onNavigationToLanguage}
                  onToggle={onToggle_}
                />
              ))}
            {showDraftInfo ? (
              <Stack
                alignItems="center"
                className="px-8 py-12 bg-bg-tertiary mt-12 w-full rounded-8"
                direction="row"
                justifyContent="start">
                <InfoOutline className="w-24 h-24" fill={INFO_ICON_FILL} />
                <Typography className="ml-8" color="text-text-paragraph" variant="body-short-04">
                  {t('Common:DraftMultilingualsInfo')}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Popover.Content>
      </Popover>
    </Stack>
  );
};

export default LanguageSelector;
